import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import * as _ from 'lodash';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, takeWhile } from 'rxjs/operators';
import { SiteConfig } from '../../../../config/site-config';
import { AppState } from '../../../app.state';
import { UserRoutes } from '../../../core/models/user-routes.model';
import { StorageService } from '../../../core/services/storage/storage.service';
import { UserRouteService } from '../../../core/services/user-routes/user-routes.service';
import { CostEstimatorComponent } from '../../../cost-estimator/cost-estimator.component';
import { DefencePopupComponent } from '../../../dashboard/defence-popup/defence-popup.component';
import {
    TrackingDetailsDialogComponent,
} from '../../../history/report-history/tracking-details-dialog/tracking-details-dialog.component';
import { RecentShipmentService } from '../../../history/services/recent-shipment.service';
import { IImageRotatorConfig } from '../../../shared/components/image-rotator/models/image-rotator-config.interface';
import { S3FileDownloaderComponent } from '../../../shared/components/s3-file-downloader/s3-file-downloader.component';
import { Customer } from '../../../shared/models/customer/customer.model';
import { NotificationType } from '../../../shared/models/notification-type';
import { IQuickTrackShipment } from '../../../shared/models/shipments/quick-track-shipment.interface';
import { AnnouncementService } from '../../../shared/services/announcement/annoucement.service';
import { IAnnouncement } from '../../../shared/services/announcement/models/announcement.interface';
import { IEvent } from '../../../shared/services/announcement/models/event.interface';
import { INews } from '../../../shared/services/announcement/models/news.interface';
import { BannerService } from '../../../shared/services/banner/banner.service';
import { BannerType } from '../../../shared/services/banner/models/banner-type.enum';
import { IBanner } from '../../../shared/services/banner/models/banner.interface';
import { ErrorHandlerService } from '../../../shared/services/error-handler/error-handler.service';
import { NotificationService } from '../../../shared/services/notification/notification.service';
import { User } from '../../../shared/services/user/models/user.model';
import { UserService } from '../../../shared/services/user/user.service';
import { UtilityService } from '../../../shared/services/utility/utility.service';
import { Package } from '../../../ship/models/package.model';
import { ShipmentService } from '../../../ship/services/shipment.service';
import {
    TermsConditionsDialogComponent,
} from '../components/terms-conditions-dialog/terms-conditions-dialog.component';

@Component({
    selector: 'upsc-dw-dashboard',
    templateUrl: './dw-dashboard.component.html',
    styleUrls: ['./dw-dashboard.component.scss'],
})
export class DwDashboardComponent implements OnInit, OnDestroy {
    @ViewChild('packagingChecklistDownloader') public packagingChecklistDownloader: S3FileDownloaderComponent;

    public user: User;
    public customer: Customer;
    public isDEUser = false;
    public isGBUser = false;
    public isEUUser: boolean = false;
    public showGiftBox = false;
    public announcements: IAnnouncement[];
    public isAnnouncementsLoading = false;

    public upcomingEvents: IEvent[];
    public isUpcomingEventsLoading = false;

    public news: INews[];
    public isNewsLoading = false;

    public banners: any[];
    public isBannersLoading = false;
    public isSideBannerAvailable = false;
    public giftClosebtn = false;
    public quickTrackShipments: IQuickTrackShipment[];
    public isGettingQuickTrackShipments = false;
    public dtOptions: any = {};
    public dtTrigger = new Subject();

    public isGettingShipmentDetails = false;
    public userRoutes: UserRoutes;
    public isDDEnabled = false;
    public getComplimentaryScores = false;
    public isUSUser = false;
    public now = new Date();
    public prevMonthLastDate = new Date(this.now.getFullYear(), this.now.getMonth(), 0).toLocaleDateString();
    public prevMonthFirstDate = new Date(this.now.getFullYear() - (this.now.getMonth() > 0 ? 0 : 1), (this.now.getMonth() - 1 + 12) % 12, 1).toLocaleDateString();

    public isPackagingChecklistDownloading = false;
    public dashboardBannerConfig: IImageRotatorConfig = SiteConfig.dashboardBanner;
    public dashboardAnnouncementConfig = SiteConfig.dashboardAnnouncement;

    private getUserRoutesSubscription: Subscription;
    private getUserLatestShipmentsSubscription: Subscription;
    private getDashboardAnnouncementsSubscription: Subscription;
    private getEventsSubscription: Subscription;
    private getNewsSubscription: Subscription;
    private getBannersSubscription: Subscription;

    public constructor(private announcementService: AnnouncementService,
                       private bannerService: BannerService,
                       private notificationService: NotificationService,
                       private recentShipmentService: RecentShipmentService,
                       private shipmentService: ShipmentService,
                       private errorHandlerService: ErrorHandlerService,
                       private userRouteService: UserRouteService,
                       private utilityService: UtilityService,
                       private userService: UserService,
                       private translateService: TranslateService,
                       private dialog: MatDialog,
                       public storageService: StorageService,
                       private readonly appState: AppState,
    ) {
    }

    public ngOnInit(): void {
        this.user = this.appState.user$();
        this.customer = this.appState.customer$();
        this.userRoutes = this.appState.userRoutes$();

        if (!this.userRoutes) {
            this.userRoutes = this.userRouteService.loadUserRoutes(this.user, this.customer);
            this.appState.userRoutes$.set(this.userRoutes);
        }

        this.handleGetUserSuccess(this.user);
        this.handleGetCustomerSuccess(this.customer);

        this.dtOptions = {
            // retrieve: true,
            autoWidth: false,
            searching: false,
            info: false,
            paging: false,
            ordering: false,
            responsive: false,
        };

        this.utilityService.clearSubscriptions([this.getUserLatestShipmentsSubscription]);
        this.isGettingQuickTrackShipments = true;
        this.getUserLatestShipmentsSubscription = this.recentShipmentService.getUserLatestShipments()
                                                      .pipe(
                                                          catchError((err) => {
                                                              this.handleGetUserLatestShipmentsFailure(err);
                                                              return of(null);
                                                          }),
                                                          takeWhile(() => !this.quickTrackShipments),
                                                      )
                                                      .subscribe(
                                                          shipments => this.handleGetUserLatestShipmentsSuccess(shipments),
                                                      );

        this.utilityService.clearSubscriptions([this.getEventsSubscription]);
        this.isUpcomingEventsLoading = true;
        this.getEventsSubscription = this.announcementService.getEvents()
                                         .pipe(
                                             catchError((err) => {
                                                 this.handleGetUpcomingEventsFailure(err);
                                                 return of(null);
                                             }),
                                             takeWhile(() => !this.upcomingEvents),
                                         )
                                         .subscribe(
                                             events => this.handleGetUpcomingEventsSuccess(events),
                                         );

        this.utilityService.clearSubscriptions([this.getNewsSubscription]);
        this.isNewsLoading = true;
        const country = this.isDEUser ? 'DE' : this.isGBUser ? 'GB' : 'US';
        this.getNewsSubscription = this.announcementService.getNews(country)
                                       .pipe(
                                           catchError((err) => {
                                               this.handleGetNewsFailure(err);
                                               return of(null);
                                           }),
                                           takeWhile(() => !this.news),
                                       )
                                       .subscribe(
                                           news => this.handleGetNewsSuccess(news),
                                       );
    }

    public ngOnDestroy(): void {
        this.utilityService.clearSubscriptions([
            this.getUserRoutesSubscription,
            this.getUserLatestShipmentsSubscription,
            this.getDashboardAnnouncementsSubscription,
            this.getEventsSubscription,
            this.getNewsSubscription,
        ]);
    }

    public openCostEstimator(event) {
        event.preventDefault();
        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            // height: '600px',
            width: '800px',
        };

        const dialogRef: MatDialogRef<CostEstimatorComponent> = this.dialog.open(CostEstimatorComponent, dialogConfig);
    }

    public openTermsConditions(event) {
        event.preventDefault();
        const dialogConfig: MatDialogConfig = {
            disableClose: true,

        };

        const dialogRef: MatDialogRef<TermsConditionsDialogComponent> = this.dialog.open(TermsConditionsDialogComponent, dialogConfig);
    }

    public open_gift() {
        document.getElementById('giftPanel').className += ' active';
        this.getComplimentaryScores = true;
        this.giftClosebtn = true;

    }

    public close_gift() {
        document.getElementById('giftPanel').classList.remove('active');
        this.getComplimentaryScores = false;
        this.giftClosebtn = false;
    }

    public getShipmentRecipient(shipment: IQuickTrackShipment) {
        if (shipment.ShipToFirstName && shipment.ShipToFirstName.trim()) {
            return `${ shipment.ShipToFirstName } ${ shipment.ShipToLastName }`;
        }

        return shipment.ShipToCompanyName;
    }

    public openTrackedShipmentDialog(event, quickTrackShipment: IQuickTrackShipment) {
        event.preventDefault();

        this.isGettingShipmentDetails = true;
        this.shipmentService.getShipment(quickTrackShipment.ShipmentId)
            .subscribe(
                shipment => this.handleGetShipmentSuccess(shipment),
                err => this.handleGetShipmentFailure(err),
            );
    }

    public getCurrentYear() {
        return (new Date()).getFullYear();
    }

    public downloadPackagingChecklist(event) {
        event.preventDefault();

        this.isPackagingChecklistDownloading = true;
        this.packagingChecklistDownloader.downloadFile(event);
    }

    public onPackagingChecklistDownloadEnded(isDownloadEnded: boolean) {
        this.isPackagingChecklistDownloading = false;
    }

    private handleGetUserSuccess(user: User): void {
        if (!user) {
            return;
        }

        this.isUSUser = user.CountryCode === 'US';
        this.isDEUser = this.userService.isDEUser(user);
        this.isGBUser = this.userService.isGBUser(user);
        this.isEUUser = this.userService.isEUUser(user.CountryCode);
    }

    private handleGetStatusCountError(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingStatusCount'),
            NotificationType.ERROR);
    }

    private handleGetStatusCountSuccess(response) {

        if ((response[0].count < 100 && response[0].count >= 0) && this.isUSUser && (this.isDDEnabled === false)) {
            this.showGiftBox = true;
        }

        if ((this.isDDEnabled === false) && this.isUSUser) {
            const dialogConfig: MatDialogConfig = {
                disableClose: true,

                data: {
                    editMode: false,
                    count: response[0].count,
                },
            };
            const dialogRef: MatDialogRef<DefencePopupComponent> = this.dialog.open(DefencePopupComponent, dialogConfig);
            dialogRef.afterClosed().subscribe((result) => {
                //      write closing scripts
            });
        }
    }

    private handleGetUserError(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingUserInfo'),
            NotificationType.ERROR);
    }

    private handleGetCustomerSuccess(response) {
        this.isDDEnabled = response?.DDEnabled;
        this.utilityService.clearSubscriptions([this.getDashboardAnnouncementsSubscription, this.getBannersSubscription]);
        this.isAnnouncementsLoading = true;
        // [MV3-2497] Removed Pipe for Shipping StationID to instead pass the int
        this.getDashboardAnnouncementsSubscription =
            this.announcementService.getAnnouncements(response.ShippingStationId)
                .pipe(takeWhile(() => !this.announcements))
                .subscribe(
                    announcements => this.handleGetDashboardAnnouncementsSuccess(announcements),
                    err => this.handleGetDashboardAnnouncementsFailure(err),
                );

        this.userService.getStatusCount(
            dayjs(this.prevMonthFirstDate).format('YYYY-MM-DD'),
            dayjs(this.prevMonthLastDate).format('YYYY-MM-DD'),
        ).subscribe(
            (response) => {
                this.handleGetStatusCountSuccess(response);
            },
            (error) => {
                this.handleGetStatusCountError(error);
            });

        this.isBannersLoading = true;
        this.getBannersSubscription = this.bannerService.getDashboardBanners(response.ShippingStationId)
                                          .pipe(takeWhile(() => !this.banners))
                                          .subscribe(
                                              banners => this.handleGetDashboardBannersSuccess(banners),
                                              err => this.handleGetDashboardBannersFailure(err),
                                          );
    }

    private handleGetCustomerError(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingCustomerInfo'),
            NotificationType.ERROR);
    }

    private handleGetUserLatestShipmentsSuccess(shipments: IQuickTrackShipment[]) {
        this.quickTrackShipments = shipments;
        this.isGettingQuickTrackShipments = false;
        this.dtTrigger.next(null);
    }

    private handleGetUserLatestShipmentsFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingQuickTrack'),
            NotificationType.ERROR);
        this.isGettingQuickTrackShipments = false;
    }

    private handleGetDashboardAnnouncementsSuccess(announcements: IAnnouncement[]) {
        this.announcements = _.sortBy(announcements, ['SortOrder']);
        this.isAnnouncementsLoading = false;
    }

    private handleGetDashboardAnnouncementsFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingAnnouncements'),
            NotificationType.ERROR);
        this.isAnnouncementsLoading = false;
    }

    private handleGetDashboardBannersSuccess(banners: IBanner[]) {
        this.dashboardBannerConfig = {
            speed: 5000,
            isAutoForward: true,
            images: [],
        };

        // [MV3-2314] use images returned from the API.
        // [MV3-2314]: ignore isActive property.
        const activeBanners = banners; // banners.filter(banner => banner.isActive);
        if (activeBanners && activeBanners.length) {
            const desktopBanner = activeBanners.find(banner => banner.BannerType === BannerType.Desktop);
            const mobileBanner = activeBanners.find(banner => banner.BannerType === BannerType.Mobile);
            const sideDashboardBanner = activeBanners.find(banner => banner.BannerType === BannerType.SideDashboard);

            this.dashboardBannerConfig.images.push({
                smallImageUrl: mobileBanner ?
                    mobileBanner.BannerURL :
                    '/assets/images/banners/PP-Members-Banner-B2B-transactaphobia-Web-Mobile-learnmore.jpg',
                mediumImageUrl: desktopBanner ?
                    desktopBanner.BannerURL :
                    '/assets/images/banners/PP-Members-banner-B2B-Transactaphobia-Web-Members-learnmore.jpg',
                largeImageUrl: desktopBanner ?
                    desktopBanner.BannerURL :
                    '/assets/images/banners/PP-Members-banner-B2B-Transactaphobia-Web-Members-learnmore.jpg',
                defaultImageUrl: desktopBanner ?
                    desktopBanner.BannerURL :
                    '/assets/images/banners/PP-Members-banner-B2B-Transactaphobia-Web-Members-learnmore.jpg',
                landingUrl: desktopBanner ?
                    desktopBanner.Hyperlink :
                    'https://upscapital.com/product-services/b2b-secure-payments/',
                sideDashboardImageUrl: sideDashboardBanner ? sideDashboardBanner.BannerURL : '',
                sideDashboardLinkUrl: sideDashboardBanner ? sideDashboardBanner.Hyperlink : '',
            });
            this.isSideBannerAvailable = sideDashboardBanner ? true : false;
        }

        this.isBannersLoading = false;
    }

    private handleGetDashboardBannersFailure(err) {
        // use default images.
        this.dashboardBannerConfig = SiteConfig.dashboardBanner;
        this.isBannersLoading = false;
    }

    private handleGetUpcomingEventsSuccess(events: IEvent[]) {
        this.upcomingEvents = <any>_.sortBy(events, ['EVentStartDate', 'EventStartTime', 'SortOrder']);
        this.isAnnouncementsLoading = false;
    }

    private handleGetUpcomingEventsFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingEvents'),
            NotificationType.ERROR);
        this.isAnnouncementsLoading = false;
    }

    private handleGetNewsSuccess(newsItems: INews[]) {
        this.news = _.sortBy(newsItems, ['SortOrder']);
        this.isNewsLoading = false;
    }

    private handleGetNewsFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingNews'),
            NotificationType.ERROR);
        this.isNewsLoading = false;
    }

    private handleGetShipmentSuccess(shipmentPackage: Package) {
        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            data: {
                shipmentPackage,
                shipmentType: 'web',
            },
        };

        const dialogRef = this.dialog.open(TrackingDetailsDialogComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(
            (result) => {
                if (!result) {
                    return;
                }
            },
        );

        this.isGettingShipmentDetails = false;
    }

    private handleGetShipmentFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingShipmentDetails'),
            NotificationType.ERROR);
        this.isGettingShipmentDetails = false;
    }
}

